import { Component, OnInit } from '@angular/core';
import {
  AgGridService,
  ConfigService,
  MarketsService
} from '@approot/services';
import {
  CellValueChangedEvent,
  ColDef,
  ColumnApi,
  GridApi,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';
import { catchError, of, take, tap } from 'rxjs';
import { CreditLinesService } from './credit-lines.service';
import { Header } from '@approot/models/ag-grid.model';
import { CreditLine } from './credit-line.interface';
import { AgGridConfig, UserSettings } from '@approot/models';
import { clone } from '@approot/utilities';
import { isEqual } from 'lodash';

@Component({
  selector: 'app-credit-lines',
  templateUrl: './credit-lines.component.html',
  styleUrls: ['./credit-lines.component.scss']
})
export class CreditLinesComponent implements OnInit {
  public columnDefs = [];
  public rowData = [];
  public changes = [];
  public processingInfo = '';
  private initialData: CreditLine[] = [];

  public readonly defaultColDef: ColDef = {
    sortable: true,
    enableRowGroup: true,
    resizable: true,
    filter: true
  };
  public sideBarDef: SideBarDef;
  public gridApi: GridApi;
  public columnApi: ColumnApi;

  constructor(
    private marketsService: MarketsService,
    private configService: ConfigService,
    private agGridService: AgGridService,
    private creditLinesService: CreditLinesService
  ) {}

  public ngOnInit(): void {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.configService
      .getUserSettings()
      .pipe(
        tap((userSettings: UserSettings) => {
          const headers = userSettings.ag_grid_config.find(
            (agGridConfig: AgGridConfig) =>
              agGridConfig.identifier === 'creditLineHeaders'
          ).headers;
          this.columnDefs = this.agGridService.formatHeaders(
            headers as Header[]
          );
        }),
        take(1)
      )
      .subscribe();

    this.creditLinesService
      .getCreditLines()
      .pipe(
        tap((creditLines: CreditLine[]) => {
          this.rowData = creditLines;
          this.initialData = clone(creditLines);
        }),
        take(1)
      )
      .subscribe();
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
  }

  public modalChanged(event: boolean): void {
    // the timeout may still be running if the user decides to close the screen early, so clear it
    clearTimeout(this.marketsService.timer);
    this.processingInfo = '';
    this.marketsService.currentAction?.unsubscribe();
    this.marketsService.showCreditLines = event;
  }

  public onCellValueChanged(event: CellValueChangedEvent): void {
    const updatedRow = event.data;
    const updatedId = updatedRow._id;

    const existingItem = this.initialData.find(x => x._id === updatedId);
    const existingIndex = this.changes.findIndex(
      change => change._id === updatedId
    );

    /**
     * if the item has changed
     * and the changed again matching its initial state,
     * remove it from the changes array
     */
    if (existingItem && isEqual(updatedRow, existingItem)) {
      if (existingIndex > -1) {
        this.changes.splice(existingIndex, 1);
      }
      return;
    }

    if (existingIndex > -1) {
      this.changes[existingIndex] = updatedRow;
    } else {
      this.changes.push(updatedRow);
    }
  }

  public saveCreditLinesChanges(): void {
    this.processingInfo = 'Processing';

    this.creditLinesService
      .setCreditLines(this.changes)
      .pipe(
        tap(() => {
          this.processingInfo = 'Saved';
          this.changes = [];
          this.initialData = clone(this.rowData);
        }),
        catchError(e => {
          this.processingInfo = 'Failed';
          return of(e.message);
        }),
        take(1)
      )
      .subscribe();
  }

  public firstDataRendered(): void {
    this.resizeColumns();
  }

  private resizeColumns(skipHeader = false): void {
    const colIds = this.columnApi.getColumns().map(column => column.getColId());
    this.columnApi.autoSizeColumns(colIds, skipHeader);
  }

  get showCreditLines() {
    return this.marketsService.showCreditLines;
  }

  set showCreditLines(val: boolean) {
    this.marketsService.showCreditLines = val;
  }
}
